import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModuleLayout from '../../layouts/ModuleLayout';
import {
  Plug,
  MessageSquare,
  Check,
  X,
  Settings,
  RefreshCw,
  ExternalLink,
  Search,
  Filter,
  ChevronDown,
  AlertCircle,
  Zap,
} from 'lucide-react';

/**
 * Integrations Hub Page
 * Manage all third-party integrations for the restaurant
 */

// Integration categories with their integrations
const INTEGRATION_CATEGORIES = [
  {
    id: 'communication',
    name: 'Communication',
    icon: MessageSquare,
    description: 'Send bills & notifications to customers',
    integrations: [
      {
        id: 'whatsapp',
        name: 'WhatsApp',
        description: 'Link your number to send bills via WhatsApp',
        logo: '💬',
        status: 'connected',
      },
    ],
  },
];

// Integration Card Component
function IntegrationCard({ integration, onConfigure }) {
  const isConnected = integration.status === 'connected';

  return (
    <div
      className={`bg-white rounded-xl border ${
        isConnected ? 'border-teal-200' : 'border-slate-200'
      } p-4 hover:shadow-md transition-all group`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
              isConnected ? 'bg-teal-50' : 'bg-slate-50'
            }`}
          >
            {integration.logo}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">{integration.name}</h3>
              {integration.popular && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">
                  POPULAR
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">{integration.description}</p>
          </div>
        </div>
        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            isConnected
              ? 'bg-teal-100 text-teal-700'
              : 'bg-slate-100 text-slate-500'
          }`}
        >
          {isConnected ? (
            <>
              <Check size={12} />
              Connected
            </>
          ) : (
            <>
              <X size={12} />
              Not Connected
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={() => onConfigure(integration)}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            isConnected
              ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              : 'bg-teal-600 text-white hover:bg-teal-700'
          }`}
        >
          {isConnected ? (
            <>
              <Settings size={14} />
              Configure
            </>
          ) : (
            <>
              <Zap size={14} />
              Connect
            </>
          )}
        </button>
        {isConnected && (
          <button
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            title="View Details"
          >
            <ExternalLink size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// Category Section Component
function CategorySection({ category, onConfigure }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = category.icon;
  const connectedCount = category.integrations.filter(
    (i) => i.status === 'connected'
  ).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Category Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <Icon size={20} className="text-teal-600" />
          </div>
          <div className="text-left">
            <h2 className="font-semibold text-slate-900">{category.name}</h2>
            <p className="text-sm text-slate-500">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {connectedCount > 0 && (
            <span className="px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
              {connectedCount} connected
            </span>
          )}
          <ChevronDown
            size={20}
            className={`text-slate-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Integrations Grid */}
      {isExpanded && (
        <div className="px-5 pb-5 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {category.integrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConfigure={onConfigure}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntegrationsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Filter integrations based on search and status
  const filteredCategories = INTEGRATION_CATEGORIES.map((category) => ({
    ...category,
    integrations: category.integrations.filter((integration) => {
      const matchesSearch =
        !searchQuery ||
        integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        integration.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || integration.status === statusFilter;
      return matchesSearch && matchesStatus;
    }),
  })).filter((category) => category.integrations.length > 0);

  // Stats
  const totalIntegrations = INTEGRATION_CATEGORIES.reduce(
    (acc, cat) => acc + cat.integrations.length,
    0
  );
  const connectedCount = INTEGRATION_CATEGORIES.reduce(
    (acc, cat) =>
      acc + cat.integrations.filter((i) => i.status === 'connected').length,
    0
  );

  const navigate = useNavigate();

  const handleConfigure = (integration) => {
    if (integration.id === 'whatsapp') {
      navigate('/integrations/whatsapp');
      return;
    }
    alert(`Configure ${integration.name} — Coming Soon!`);
  };

  return (
    <ModuleLayout>
      <div className="flex flex-col h-full bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-slate-900">Integrations</h1>
              {/* Quick Stats */}
              <div className="hidden md:flex items-center gap-3 text-sm">
                <span className="text-slate-500">{totalIntegrations} available</span>
                <span className="flex items-center gap-1 text-teal-600">
                  <Check size={14} />
                  {connectedCount} connected
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                title="Refresh"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-2.5">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 border border-slate-200"
              >
                <Filter size={14} />
                {statusFilter === 'all'
                  ? 'All Status'
                  : statusFilter === 'connected'
                  ? 'Connected'
                  : 'Not Connected'}
                <ChevronDown size={14} />
              </button>
              {showFilterDropdown && (
                <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                  {['all', 'connected', 'not_connected'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                        statusFilter === status
                          ? 'text-teal-600 bg-teal-50'
                          : 'text-slate-700'
                      }`}
                    >
                      {status === 'all'
                        ? 'All Status'
                        : status === 'connected'
                        ? 'Connected'
                        : 'Not Connected'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Info Banner */}
          <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Plug size={20} className="text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-teal-900">Connect Your Tools</h3>
              <p className="text-sm text-teal-700 mt-1">
                Integrate with popular delivery platforms, payment gateways, and
                accounting software to streamline your restaurant operations.
              </p>
            </div>
          </div>

          {/* Categories */}
          {filteredCategories.length > 0 ? (
            <div className="space-y-6">
              {filteredCategories.map((category) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  onConfigure={handleConfigure}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No Integrations Found
              </h3>
              <p className="text-slate-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-slate-200 px-6 py-2">
          <p className="text-xs text-slate-500">
            Need a custom integration?{' '}
            <a href="#" className="text-teal-600 hover:text-teal-700 font-medium">
              Contact our team
            </a>
          </p>
        </div>
      </div>
    </ModuleLayout>
  );
}
